import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import GarageListPanel from "@/components/GarageListPanel";
import NearMePanel from "@/components/NearMePanel";
import HistoryPanel from "@/components/HistoryPanel";
import OrdersPanel from "@/components/OrdersPanel";
import SettingsPanel from "@/components/SettingsPanel";
import ProspectingPanel from "@/components/ProspectingPanel";
import ReviewPanel from "@/components/ReviewPanel";
import JourneyPanel from "@/components/JourneyPanel";  // eslint-disable-line no-unused-vars
import UsersPanel from "@/components/UsersPanel";
import UsagePanel from "@/components/UsagePanel";
import OrganisationsPanel from "@/components/OrganisationsPanel";
import GarageDetail from "@/components/GarageDetail";
import AddGarageForm from "@/components/AddGarageForm";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Store, Radar, Route, CalendarCheck, MapPin, Footprints, Users, DollarSign, LogOut, Menu, X, Search, Sun, Settings as SettingsIcon, ChevronDown, Mail, Shield, Receipt, Building2, CheckCircle2,
} from "lucide-react";

const TABS = [
  { id: "mine", label: "My garages", icon: Store, manager: false },
  { id: "prospects", label: "Prospects", icon: Radar, manager: false },
  { id: "near", label: "Around me", icon: MapPin, manager: false },
  { id: "orders", label: "Orders", icon: Receipt, manager: false },
  { id: "history", label: "History", icon: Footprints, manager: false },
  { id: "team", label: "Team", icon: Users, manager: true },
  { id: "settings", label: "Settings", icon: SettingsIcon, manager: true },
  { id: "usage", label: "Usage & cost", icon: DollarSign, manager: true },
  { id: "orgs", label: "Organisations", icon: Building2, manager: true, owner: true },
];

// bottom bar on phones
const MOBILE_TABS = ["mine", "prospects", "near", "orders", "history"];

export default function AppShell() {
  const { user, logout, switchOrg } = useAuth();
  const isManager = user?.role === "manager";
  const [tab, setTab] = useState("mine"); // 3.6 — open on My garages (customers), not My Day
  const [detailId, setDetailId] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [addBusy, setAddBusy] = useState(false);
  const [findOpen, setFindOpen] = useState(false);
  const [reviewSweep, setReviewSweep] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [userMenu, setUserMenu] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [orgs, setOrgs] = useState([]);
  const [orgMenu, setOrgMenu] = useState(false);
  const firstName = (user?.name || user?.email || "").split(/[ @]/)[0];

  useEffect(() => {
    if (user?.is_owner) api.get("/organisations").then((r) => setOrgs(r.data)).catch(() => {});
  }, [user?.is_owner]);

  useEffect(() => {
    // Only greet after a real login — not on every mount, reload or org switch.
    let justLoggedIn = false;
    try { justLoggedIn = sessionStorage.getItem("jam_just_logged_in") === "1"; } catch { /* ignore */ }
    if (justLoggedIn && (user?.name || user?.email)) {
      toast.success(`Signed in as ${user.name || user.email}`);
      try { sessionStorage.removeItem("jam_just_logged_in"); } catch { /* ignore */ }
    }
    // eslint-disable-next-line
  }, []);
  useEffect(() => {
    const label = TABS.find((t) => t.id === tab)?.label || "";
    document.title = label ? `${label} · JAM Field Sales` : "JAM Field Sales";
  }, [tab]);

  const tabs = TABS.filter((t) => (!t.manager || isManager) && (!t.owner || user?.is_owner));
  const bump = () => setRefreshKey((k) => k + 1);

  const addGarage = async (payload) => {
    setAddBusy(true);
    try {
      const { data } = await api.post("/garages", payload);
      toast.success("Garage added");
      setAddOpen(false);
      bump();
      if (data?.id) setDetailId(data.id); // 3.3 — open the new garage so you can record right away
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d && d.code === "duplicate_garage") {
        // 1.1 — don't dead-end; tell them where it is and offer to open it
        setAddOpen(false);
        toast.error(d.message, { action: { label: "Open it", onClick: () => setDetailId(d.garage_id) }, duration: 8000 });
      } else {
        toast.error(formatApiError(d));
      }
    } finally { setAddBusy(false); }
  };

  const go = (id) => { setTab(id); setMenuOpen(false); setReviewSweep(null); setFindOpen(false); };

  // 2.2 — Android/browser back closes the open panel instead of leaving the app
  const overlayOpen = !!(detailId || addOpen || findOpen || reviewSweep);
  useEffect(() => {
    if (!overlayOpen) return;
    window.history.pushState({ jamOverlay: true }, "");
    const onPop = () => { setAddOpen(false); setDetailId(null); setReviewSweep(null); setFindOpen(false); };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [overlayOpen]);

  return (
    <div className="min-h-screen bg-[#F9FAFB] pb-20 md:pb-0">
      <header className="bg-[#1E3A8A] text-white sticky top-0 z-40">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 h-14 md:h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <button onClick={() => setMenuOpen((m) => !m)} data-testid="mobile-menu-btn"
              className="md:hidden p-2 -ml-2">{menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}</button>
            <img src="/jam-shield.png" alt="JAM" className="h-9 w-9 object-contain shrink-0 bg-white rounded-md p-0.5" />
            <span className="hidden md:block text-xs lg:text-sm font-black tracking-tight leading-none uppercase max-w-[260px]">Field Sales<br />Command</span>
            {user?.organisation_name && <span className={`md:hidden text-sm font-bold truncate ${user.is_acting ? "text-[#FCD34D]" : "text-white/85"}`} data-testid="org-name-mobile">{user.is_acting ? `Viewing ${user.organisation_name}` : user.organisation_name}</span>}
          </div>
          <div className="flex items-center gap-3">
            {user?.organisation_name && (
              user?.is_owner ? (
                <Popover open={orgMenu} onOpenChange={setOrgMenu}>
                  <PopoverTrigger asChild>
                    <button data-testid="org-switcher"
                      className={`hidden md:flex items-center gap-1.5 px-3 h-10 rounded-md font-bold text-sm max-w-[300px] transition-colors ${user.is_acting ? "bg-[#F59E0B] text-[#111827] hover:bg-[#FBBF24]" : "bg-white/10 text-white hover:bg-white/20"}`}>
                      <Building2 className="w-4 h-4 shrink-0" />
                      <span className="truncate">{user.is_acting ? `Viewing ${user.organisation_name}` : user.organisation_name}</span>
                      <ChevronDown className="w-4 h-4 shrink-0" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent align="end" className="w-72 p-1.5" data-testid="org-switcher-menu">
                    <div className="px-2 py-1.5 text-xs font-bold uppercase text-[#9CA3AF]">Switch organisation</div>
                    {orgs.map((o) => {
                      const active = o.id === user.organisation_id;
                      return (
                        <button key={o.id} onClick={() => { setOrgMenu(false); if (!active) switchOrg(o.id); }} data-testid={`switch-org-${o.id}`}
                          className={`w-full text-left px-2 h-11 rounded-md flex items-center gap-2 text-sm font-bold ${active ? "bg-[#EEF2FF] text-[#1E3A8A]" : "text-[#374151] hover:bg-[#F9FAFB]"}`}>
                          <Building2 className="w-4 h-4 shrink-0" />
                          <span className="truncate flex-1">{o.name}</span>
                          {o.is_default && <span className="text-[10px] uppercase bg-[#EEF2FF] text-[#1E3A8A] px-1.5 py-0.5 rounded-full shrink-0">Home</span>}
                          {active && <CheckCircle2 className="w-4 h-4 text-[#1E3A8A] shrink-0" />}
                        </button>
                      );
                    })}
                  </PopoverContent>
                </Popover>
              ) : (
                <span className="hidden md:flex items-center gap-1.5 bg-white/10 px-3 h-10 rounded-md font-bold text-sm max-w-[280px]" data-testid="org-name">
                  <Building2 className="w-4 h-4 shrink-0" /> <span className="truncate">{user.organisation_name}</span>
                </span>
              )
            )}
            <Popover open={userMenu} onOpenChange={setUserMenu}>
              <PopoverTrigger asChild>
                <button data-testid="user-menu-btn"
                  className="flex items-center gap-2 bg-white/10 hover:bg-white/20 px-3 h-10 rounded-md font-bold text-sm transition-colors max-w-[180px]">
                  <span className="truncate">{firstName || "Account"}</span>
                  <ChevronDown className="w-4 h-4 shrink-0" />
                </button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-64 p-2" data-testid="user-menu">
                <div className="px-2 py-2 border-b border-[#F3F4F6] mb-1">
                  <div className="font-black text-[#111827] truncate">{user?.name || firstName}</div>
                  <div className="text-xs text-[#6B7280] flex items-center gap-1.5 mt-0.5 truncate"><Mail className="w-3.5 h-3.5 shrink-0" /> {user?.email}</div>
                  <div className="text-xs text-[#6B7280] flex items-center gap-1.5 mt-0.5 capitalize"><Shield className="w-3.5 h-3.5 shrink-0" /> {user?.role}{user?.is_owner ? " · owner" : ""}</div>
                  {user?.organisation_name && <div className="text-xs text-[#6B7280] flex items-center gap-1.5 mt-0.5 truncate"><Building2 className="w-3.5 h-3.5 shrink-0" /> {user.organisation_name}</div>}
                </div>
                {user?.is_owner && orgs.length > 0 && (
                  <div className="md:hidden border-b border-[#F3F4F6] mb-1 pb-1" data-testid="org-switcher-mobile">
                    <div className="px-2 py-1 text-xs font-bold uppercase text-[#9CA3AF]">Switch organisation</div>
                    {orgs.map((o) => {
                      const active = o.id === user.organisation_id;
                      return (
                        <button key={o.id} onClick={() => { setUserMenu(false); if (!active) switchOrg(o.id); }} data-testid={`switch-org-m-${o.id}`}
                          className={`w-full text-left px-2 h-11 rounded-md flex items-center gap-2 text-sm font-bold ${active ? "bg-[#EEF2FF] text-[#1E3A8A]" : "text-[#374151]"}`}>
                          <Building2 className="w-4 h-4 shrink-0" />
                          <span className="truncate flex-1">{o.name}</span>
                          {o.is_default && <span className="text-[10px] uppercase bg-[#EEF2FF] text-[#1E3A8A] px-1.5 py-0.5 rounded-full shrink-0">Home</span>}
                          {active && <CheckCircle2 className="w-4 h-4 shrink-0" />}
                        </button>
                      );
                    })}
                  </div>
                )}
                <button onClick={logout} data-testid="logout-btn"
                  className="w-full flex items-center gap-2 px-3 h-11 rounded-md text-sm font-bold text-[#B91C1C] hover:bg-[#FEE2E2]">
                  <LogOut className="w-4 h-4" /> Log out
                </button>
              </PopoverContent>
            </Popover>
          </div>
        </div>

        {/* desktop tabs */}
        <div className="hidden md:block border-t border-white/10">
          <div className="max-w-[1400px] mx-auto px-6 flex gap-1 overflow-x-auto">
            {tabs.map((t) => (
              <button key={t.id} onClick={() => go(t.id)} data-testid={`view-${t.id}`}
                className={`flex items-center gap-2 px-4 h-12 font-bold text-sm whitespace-nowrap border-b-2 transition-colors ${tab === t.id ? "border-white text-white" : "border-transparent text-white/60 hover:text-white"}`}>
                <t.icon className="w-4 h-4" /> {t.label}
              </button>
            ))}
          </div>
        </div>
      </header>

      {user?.is_acting && (
        <div className="bg-[#F59E0B] text-[#111827] text-xs sm:text-sm font-bold text-center py-2 px-4" data-testid="acting-banner">
          Viewing {user.organisation_name} — not your own organisation. Visits & orders you record go here.
        </div>
      )}
      {/* mobile slide-down menu (manager extras + everything) */}
      {menuOpen && (
        <div className="md:hidden bg-white border-b border-[#E5E7EB] shadow-sm" data-testid="mobile-menu">
          {tabs.map((t) => (
            <button key={t.id} onClick={() => go(t.id)} data-testid={`menu-${t.id}`}
              className={`w-full px-5 py-4 flex items-center gap-3 font-bold text-sm border-b border-[#F3F4F6] ${tab === t.id ? "text-[#1E3A8A] bg-[#EEF2FF]" : "text-[#374151]"}`}>
              <t.icon className="w-5 h-5" /> {t.label}
            </button>
          ))}
        </div>
      )}

      <main className="max-w-[1400px] mx-auto px-4 md:px-6 py-5 md:py-6">
        {tab === "mine" && (
          <GarageListPanel
            scope="mine" title="My garages" subtitle="WhatsApp group created — your live customers"
            isManager={isManager} refreshKey={refreshKey}
            onOpenDetail={setDetailId} onAdd={() => setAddOpen(true)}
          />
        )}

        {tab === "prospects" && (
          reviewSweep ? (
            <ReviewPanel sweep={reviewSweep} onBack={() => { setReviewSweep(null); bump(); }} />
          ) : findOpen ? (
            <div>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h1 className="text-2xl md:text-3xl font-black tracking-tight text-[#1E3A8A]">Find new garages</h1>
                  <p className="text-sm text-[#6B7280] mt-1">Search an area or use your location, then scan it.</p>
                </div>
                <button onClick={() => setFindOpen(false)} data-testid="close-find"
                  className="h-11 px-4 rounded-lg border border-[#D1D5DB] bg-white font-bold text-sm">Back to list</button>
              </div>
              <ProspectingPanel onOpenReview={setReviewSweep} />
            </div>
          ) : (
            <GarageListPanel
              scope="prospects" title="Prospects" subtitle="Your leads — visit them, then convert to My garages"
              isManager={isManager} refreshKey={refreshKey}
              onOpenDetail={setDetailId} onAdd={() => setAddOpen(true)}
              extraAction={
                user?.scanning_enabled ? (
                  <button onClick={() => setFindOpen(true)} data-testid="open-find"
                    className="h-11 px-4 rounded-lg bg-[#111827] text-white font-bold text-sm flex items-center gap-2">
                    <Search className="w-4 h-4" /> Find new
                  </button>
                ) : null
              }
            />
          )
        )}

        {tab === "near" && <NearMePanel onOpenDetail={setDetailId} />}
        {tab === "orders" && <OrdersPanel isManager={isManager} onOpenDetail={setDetailId} />}
        {tab === "history" && <HistoryPanel isManager={isManager} salesEmail={user?.email} onOpenDetail={setDetailId} />}
        {tab === "settings" && isManager && <SettingsPanel />}
        {tab === "team" && isManager && <UsersPanel meEmail={user?.email} />}
        {tab === "usage" && isManager && <UsagePanel />}
        {tab === "orgs" && user?.is_owner && <OrganisationsPanel />}
      </main>

      {/* mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 h-16 bg-white border-t border-[#E5E7EB] flex z-40" data-testid="bottom-nav">
        {MOBILE_TABS.map((id) => {
          const t = TABS.find((x) => x.id === id);
          return (
            <button key={id} onClick={() => go(id)} data-testid={`nav-${id}`}
              className={`flex-1 flex flex-col items-center justify-center gap-0.5 min-h-[56px] ${tab === id ? "text-[#1E3A8A]" : "text-[#9CA3AF]"}`}>
              <t.icon className="w-6 h-6" strokeWidth={2.5} />
              <span className="text-[10px] font-bold">{t.label}</span>
            </button>
          );
        })}
      </nav>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-xl font-black text-[#1E3A8A]">Add a garage</DialogTitle></DialogHeader>
          <AddGarageForm onSubmit={addGarage} busy={addBusy} />
        </DialogContent>
      </Dialog>

      <GarageDetail
        garageId={detailId}
        open={!!detailId}
        onClose={() => setDetailId(null)}
        isManager={isManager}
        onChanged={bump}
        onOpenDetail={(id) => setDetailId(id)}
      />
    </div>
  );
}
